import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { track, trackOnce } from '../../usage';
import {
    DEFAULT_REFERENCE_PITCH,
    MAX_REFERENCE_PITCH,
    MIN_REFERENCE_PITCH,
    centsBetween,
    describeFrequency,
    detectPitch,
    midiToFrequency,
    midiToNote,
    prettyNoteName,
} from './pitch';
import {
    DEFAULT_TUNER_INSTRUMENTS,
    MAX_NAME_LENGTH,
    MAX_NOTE,
    MIN_NOTE,
    closestString,
    isTunable,
    labelsForNotes,
    makeCustomTuning,
    stringsForTuning,
    tuningsForInstrument,
} from './tunings';
import { loadReferenceNote, playbackRate, referenceSoundFor } from './samples';
import TunerTrace from './trace';
import { isAudioSupported, isMicrophoneSupported, microphone, resumeAudio } from '../practice-dock/audio';

// Within this many cents a string counts as in tune.
const IN_TUNE_CENTS = 5
// How often to analyse the microphone (ms). The detector is O(n * lag), so
// there's no point running it on every animation frame.
const ANALYSE_EVERY = 33
// Readings are median-smoothed over this many analyses.
const SMOOTHING = 5
// After this long without a pitch, start a fresh reading for the next pluck.
const RESET_AFTER = 300
// After this long without a pitch, clear the display.
const HOLD_FOR = 1500

// Instrument selection that means "no strings, just show the nearest note".
const CHROMATIC = 'chromatic'
// Values of the tuning menu's action items.
const NEW_TUNING = '__new'
const EDIT_TUNING = '__edit'
// Common concert pitches offered as one-tap choices.
const REFERENCE_PITCH_PRESETS = [432, 440, 442, 444]

const isValidReferencePitch = (value) => Number.isInteger(value) && value >= MIN_REFERENCE_PITCH && value <= MAX_REFERENCE_PITCH

const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b)
    return sorted[Math.floor(sorted.length / 2)]
}

const errorStatus = (error) => {
    if (error && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) {
        return 'denied'
    }
    if (error && (error.name === 'NotFoundError' || error.name === 'OverconstrainedError')) {
        return 'no-microphone'
    }
    return 'error'
}

const STATUS_MESSAGES = {
    unsupported: () => gettext('Your browser does not support microphone input. You can still tap a string to hear its note.'),
    denied: () => gettext('Microphone access is blocked. Allow the microphone for this site in your browser settings and try again.'),
    'no-microphone': () => gettext('No microphone was found.'),
    error: () => gettext('Could not start the microphone.'),
}

const noteList = (labels) => labels.map(prettyNoteName).join(' ')

function NoteName({ name, octave }) {
    return <>{prettyNoteName(name)}<sub>{octave}</sub></>
}

export default function Tuner({
    instruments = [],
    instrumentPreference = null,
    autoStart = true,
    // Saved in the user's profile as one object, so a change that touches
    // several of these is saved together:
    //   mode: 'chromatic' if that's what they last used
    //   tunings: the tuning last used per instrument, { guitar: 'drop-d' }
    //   custom: their own tunings (see makeCustomTuning)
    //   referencePitch: A4 in Hz
    settings = {},
    onChangeSettings = () => {},
    // Custom tunings and a custom reference pitch are for subscribers.
    isSubscriber = false,
    onRequireSubscription = () => {},
}) {
    const customTunings = Array.isArray(settings.custom) ? settings.custom : []
    const savedTunings = settings.tunings && typeof settings.tunings === 'object' ? settings.tunings : {}
    const updateSettings = (changes) => onChangeSettings({ ...settings, ...changes })

    const tunableInstruments = useMemo(() => {
        const available = instruments.filter(isTunable)
        return available.length > 0 ? available : DEFAULT_TUNER_INSTRUMENTS
    }, [instruments])

    // Start on the instrument the song is shown for, or chromatic if that's
    // what the user last used or their instrument has no strings (piano).
    const [instrumentName, setInstrumentName] = useState(() => {
        if (settings.mode === CHROMATIC) {
            return CHROMATIC
        }
        const preferred = tunableInstruments.find(i => i.id === instrumentPreference)
        if (preferred) {
            return preferred.safe_name
        }
        if (instruments.some(i => i.id === instrumentPreference)) {
            return CHROMATIC
        }
        return tunableInstruments[0].safe_name
    })
    const isChromatic = instrumentName === CHROMATIC
    const instrument = isChromatic ? null :
        tunableInstruments.find(i => i.safe_name === instrumentName) || tunableInstruments[0]

    const tunings = useMemo(() => instrument ? tuningsForInstrument(instrument, customTunings) : [], [instrument, customTunings])
    const selectedTuning = instrument ?
        tunings.find(t => t.id === savedTunings[instrument.safe_name]) || tunings[0] :
        null

    // A custom tuning being created or edited: { id, name, notes }. While it's
    // open the tuner tunes to the draft, so strings can be set by ear.
    const [draft, setDraft] = useState(null)

    const [showReferencePitch, setShowReferencePitch] = useState(false)
    // Everyone else always tunes to A4 = 440 Hz.
    const a4 = isSubscriber && isValidReferencePitch(settings.referencePitch) ? settings.referencePitch : DEFAULT_REFERENCE_PITCH

    let activeTuning = selectedTuning
    if (draft) {
        activeTuning = { notes: draft.notes, labels: labelsForNotes(draft.notes) }
    }
    const strings = activeTuning ? stringsForTuning(activeTuning, a4) : []
    const referenceSound = referenceSoundFor(instrument)

    const [status, setStatus] = useState(isMicrophoneSupported() ? 'idle' : 'unsupported')
    const [lockedString, setLockedString] = useState(null)
    const [frequency, setFrequency] = useState(null)
    const [stale, setStale] = useState(false)

    // The AudioContext and microphone are the practice dock's (shared with
    // its other tools); the tuner holds its claim on the microphone in
    // audio.current while it listens.
    const audio = useRef({})

    const stop = useCallback(() => {
        const a = audio.current
        if (a.frame) {
            cancelAnimationFrame(a.frame)
        }
        if (a.analyser) {
            microphone.disconnect(a.analyser)
        }
        if (a.context && a.updateStatus) {
            a.context.removeEventListener('statechange', a.updateStatus)
        }
        if (a.reference) {
            try {
                a.reference.source.stop()
            } catch (e) {
                // Already stopped.
            }
        }
        microphone.release(a)
        audio.current = {}
    }, [])

    // Browsers only let audio start from a user gesture; resuming is a
    // no-op when it's already running.
    const getContext = () => {
        const a = audio.current
        a.context = resumeAudio()
        return a.context
    }

    const listen = (analyser, sampleRate) => {
        const a = audio.current
        const buffer = new Float32Array(analyser.fftSize)
        const bytes = analyser.getFloatTimeDomainData ? null : new Uint8Array(analyser.fftSize)
        let history = []
        let lastAnalysed = 0
        let lastHeard = 0

        const tick = (now) => {
            a.frame = requestAnimationFrame(tick)
            if (now - lastAnalysed < ANALYSE_EVERY) {
                return
            }
            lastAnalysed = now

            if (bytes) {
                analyser.getByteTimeDomainData(bytes)
                bytes.forEach((b, i) => { buffer[i] = (b - 128) / 128 })
            } else {
                analyser.getFloatTimeDomainData(buffer)
            }

            const pitch = detectPitch(buffer, sampleRate)
            if (pitch) {
                if (now - lastHeard > RESET_AFTER) {
                    history = []
                }
                lastHeard = now
                history = [...history, pitch.frequency].slice(-SMOOTHING)
                setFrequency(median(history))
                setStale(false)
            } else if (lastHeard && now - lastHeard > HOLD_FOR) {
                lastHeard = 0
                history = []
                setFrequency(null)
                setStale(false)
            } else if (lastHeard) {
                setStale(true)
            }
        }
        a.frame = requestAnimationFrame(tick)
    }

    const start = async () => {
        if (!isMicrophoneSupported()) {
            setStatus('unsupported')
            return
        }
        if (audio.current.analyser) {
            // We have the microphone but the browser held the audio context
            // back until a tap; this click is that tap.
            getContext()
            return
        }
        setStatus('starting')
        const a = audio.current
        const context = getContext()
        try {
            // Voice processing (echo cancellation, noise suppression, gain
            // control) smears pitch, so ask for the raw signal.
            await microphone.acquire(a, { echoCancellation: false })
            if (audio.current !== a) {
                return // closed while the permission prompt was open
            }
            const analyser = context.createAnalyser()
            analyser.fftSize = 4096
            microphone.connect(analyser)
            a.analyser = analyser

            listen(analyser, context.sampleRate)
            a.updateStatus = () => setStatus(context.state === 'running' ? 'listening' : 'suspended')
            context.addEventListener('statechange', a.updateStatus)
            a.updateStatus()
            track('tuner_started', { instrument: instrumentName })
        } catch (error) {
            if (audio.current === a) {
                setStatus(errorStatus(error))
            }
        }
    }

    const playReference = async (midi) => {
        if (!isAudioSupported()) {
            return
        }
        trackOnce('tuner_reference_played')
        // Get the context now: browsers only let audio start from the tap itself.
        const context = getContext()
        const a = audio.current
        const tap = (a.referenceTap || 0) + 1
        a.referenceTap = tap

        let sample = null
        try {
            sample = await loadReferenceNote(referenceSound, midi)
        } catch (e) {
            // Couldn't fetch or decode the recording; the synth tone stands in.
        }
        if (audio.current !== a || a.referenceTap !== tap) {
            // Closed, or another string was tapped while this one loaded.
            return
        }

        const now = context.currentTime
        if (a.reference) {
            // Cut the previous note short so quick taps don't pile up.
            try {
                a.reference.gain.gain.cancelScheduledValues(now)
                a.reference.gain.gain.setTargetAtTime(0.0001, now, 0.02)
                a.reference.source.stop(now + 0.1)
            } catch (e) {
                // Already stopped.
            }
        }
        const gain = context.createGain()
        gain.connect(context.destination)
        let source
        if (sample) {
            source = context.createBufferSource()
            source.buffer = sample.buffer
            source.playbackRate.value = playbackRate(sample, midi, a4)
            source.connect(gain)
            source.start(now, sample.offset)
        } else {
            source = context.createOscillator()
            source.type = 'triangle'
            source.frequency.value = midiToFrequency(midi, a4)
            // Quick attack, long decay: closer to a plucked string than a beep.
            gain.gain.setValueAtTime(0.0001, now)
            gain.gain.exponentialRampToValueAtTime(0.4, now + 0.02)
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 2)
            source.connect(gain)
            source.start(now)
            source.stop(now + 2)
        }
        a.reference = { source, gain }
    }

    const selectString = (index) => {
        if (lockedString === index) {
            setLockedString(null)
            return
        }
        setLockedString(index)
        playReference(strings[index].midi)
    }

    const selectInstrument = (name) => {
        if ((name === CHROMATIC) !== isChromatic) {
            updateSettings({ mode: name === CHROMATIC ? CHROMATIC : 'instrument' })
        }
        setInstrumentName(name)
        setLockedString(null)
        setDraft(null)
    }

    const selectTuning = (id) => {
        updateSettings({ tunings: { ...savedTunings, [instrument.safe_name]: id } })
        setLockedString(null)
    }

    const startNewTuning = () => {
        if (!isSubscriber) {
            onRequireSubscription()
            return
        }
        setDraft({ id: null, name: '', notes: [...selectedTuning.notes] })
        setLockedString(null)
    }

    const chooseFromTuningMenu = (value) => {
        if (value === NEW_TUNING) {
            startNewTuning()
        } else if (value === EDIT_TUNING) {
            editTuning()
        } else {
            selectTuning(value)
        }
    }

    const editTuning = () => {
        setDraft({ id: selectedTuning.id, name: selectedTuning.name, notes: [...selectedTuning.notes] })
        setLockedString(null)
    }

    const shiftDraftString = (index, semitones) => {
        const midi = Math.max(MIN_NOTE, Math.min(MAX_NOTE, draft.notes[index] + semitones))
        setDraft({ ...draft, notes: draft.notes.map((n, i) => i === index ? midi : n) })
        setLockedString(index)
        playReference(midi)
    }

    const saveDraft = () => {
        if (!isSubscriber) {
            onRequireSubscription()
            return
        }
        const name = draft.name.trim() || noteList(labelsForNotes(draft.notes))
        const tuning = makeCustomTuning(draft.id, instrument, name, draft.notes)
        const custom = customTunings.some(t => t.id === tuning.id) ?
            customTunings.map(t => t.id === tuning.id ? tuning : t) :
            [...customTunings, tuning]
        updateSettings({ custom, tunings: { ...savedTunings, [instrument.safe_name]: tuning.id } })
        setDraft(null)
        setLockedString(null)
        track('tuner_custom_tuning_saved')
    }

    const deleteDraft = () => {
        if (!window.confirm(gettext('Delete this tuning?'))) {
            return
        }
        updateSettings({
            custom: customTunings.filter(t => t.id !== draft.id),
            tunings: { ...savedTunings, [instrument.safe_name]: 'standard' },
        })
        setDraft(null)
        setLockedString(null)
    }

    const toggleReferencePitch = () => {
        if (!isSubscriber) {
            onRequireSubscription()
            return
        }
        setShowReferencePitch(!showReferencePitch)
    }

    const changeReferencePitch = (value) => {
        updateSettings({ referencePitch: Math.max(MIN_REFERENCE_PITCH, Math.min(MAX_REFERENCE_PITCH, value)) })
    }

    useEffect(() => {
        if (autoStart) {
            start()
        }
        return stop
    }, [])

    // Fetch the recorded notes for these strings before they're tapped. Keyed
    // on the notes as a string: the strings array is new on every render.
    const stringNotes = strings.map(s => s.midi).join(' ')
    useEffect(() => {
        if (stringNotes) {
            stringNotes.split(' ').forEach(midi => loadReferenceNote(referenceSound, Number(midi)).catch(() => {}))
        }
    }, [referenceSound, stringNotes])

    // Work out what the current pitch should be compared against: the string
    // the user picked, the nearest string, or (chromatic) the nearest note.
    let target = null
    let targetName = null
    let activeString = null
    if (frequency) {
        if (strings.length > 0) {
            activeString = lockedString !== null && lockedString < strings.length ? lockedString : closestString(strings, frequency)
            target = strings[activeString].midi
            targetName = strings[activeString].label
        } else {
            target = describeFrequency(frequency, a4).midi
            targetName = midiToNote(target).name
        }
    }
    const cents = target !== null ? centsBetween(frequency, midiToFrequency(target, a4)) : 0
    const inTune = target !== null && Math.abs(cents) <= IN_TUNE_CENTS

    let hint = null
    if (target !== null) {
        if (inTune) {
            hint = gettext('In tune')
        } else {
            hint = cents < 0 ? gettext('Tune up') : gettext('Tune down')
        }
    }

    const presetTunings = tunings.filter(t => !t.custom)
    const ownTunings = tunings.filter(t => t.custom)

    return (
        <div className={classNames('tuner', { 'tuner--in-tune': inTune, 'tuner--stale': stale, 'tuner--waiting': target === null })}>
            <div className="tuner-selects">
                <select className="tuner-select" aria-label={gettext('Instrument')} value={instrumentName} onChange={e => selectInstrument(e.target.value)}>
                    {tunableInstruments.map(i => <option key={i.safe_name} value={i.safe_name}>{i.name}</option>)}
                    <option value={CHROMATIC}>{gettext('Chromatic')}</option>
                </select>
                {instrument && !draft &&
                    <select className="tuner-select tuner-select--tuning" aria-label={gettext('Tuning')} value={selectedTuning.id} onChange={e => chooseFromTuningMenu(e.target.value)}>
                        <optgroup label={gettext('Common tunings')}>
                            {presetTunings.map(t => <option key={t.id} value={t.id}>{t.name} · {noteList(t.labels)}</option>)}
                        </optgroup>
                        {ownTunings.length > 0 &&
                            <optgroup label={gettext('Your tunings')}>
                                {ownTunings.map(t => <option key={t.id} value={t.id}>{t.name} · {noteList(t.labels)}</option>)}
                            </optgroup>
                        }
                        <option value={NEW_TUNING}>{gettext('New tuning…')}</option>
                        {selectedTuning.custom &&
                            <option value={EDIT_TUNING}>{gettext('Edit this tuning…')}</option>
                        }
                    </select>
                }
            </div>

            <div className="tuner-display" aria-live="polite">
                <div className="tuner-note">
                    {target !== null ? <NoteName name={targetName} octave={midiToNote(target).octave} /> : '–'}
                </div>
                <div className="tuner-frequency">
                    {frequency ? `${frequency.toFixed(1)} Hz` : (status === 'listening' ? gettext('Listening…') : ' ')}
                </div>
            </div>

            <TunerTrace
                cents={target !== null ? cents : null}
                stale={stale}
                inTuneCents={IN_TUNE_CENTS}
                hint={hint}
                waitingText={isChromatic ? gettext('Play a note') : gettext('Play a string')}
            />

            {draft &&
                <div className="tuner-editor">
                    <input
                        type="text"
                        className="tuner-editor-name"
                        aria-label={gettext('Tuning name')}
                        placeholder={noteList(labelsForNotes(draft.notes))}
                        maxLength={MAX_NAME_LENGTH}
                        value={draft.name}
                        onChange={e => setDraft({ ...draft, name: e.target.value })}
                    />
                    <div className="tuner-editor-strings">
                        {draft.notes.map((midi, i) => {
                            const note = midiToNote(midi)
                            return (
                                <div key={i} className={classNames('tuner-editor-string', { active: activeString === i, 'in-tune': activeString === i && inTune })}>
                                    <button type="button" className="tuner-step" aria-label={gettext('Up a semitone')} disabled={midi >= MAX_NOTE} onClick={() => shiftDraftString(i, 1)}>▲</button>
                                    <button type="button" className="tuner-editor-note" title={`${midiToFrequency(midi, a4).toFixed(1)} Hz`} onClick={() => selectString(i)}>
                                        <NoteName name={note.name} octave={note.octave} />
                                    </button>
                                    <button type="button" className="tuner-step" aria-label={gettext('Down a semitone')} disabled={midi <= MIN_NOTE} onClick={() => shiftDraftString(i, -1)}>▼</button>
                                </div>
                            )
                        })}
                    </div>
                    <p className="tuner-help">{gettext('Use the arrows to set each string, then save the tuning.')}</p>
                    <div className="tuner-editor-actions">
                        {draft.id &&
                            <button type="button" className="tuner-link tuner-link--danger" onClick={deleteDraft}>{gettext('Delete')}</button>
                        }
                        <button type="button" className="tuner-link" onClick={() => setDraft(null)}>{gettext('Cancel')}</button>
                        <button type="button" className="btn tuner-start-button" onClick={saveDraft}>{gettext('Save tuning')}</button>
                    </div>
                </div>
            }

            {!draft && strings.length > 0 &&
                <>
                    <div className="tuner-strings">
                        <button type="button" className={classNames('tuner-string tuner-string--auto', { active: lockedString === null })} aria-pressed={lockedString === null} onClick={() => setLockedString(null)}>
                            {gettext('Auto')}
                        </button>
                        {strings.map((string, i) =>
                            <button
                                key={`${instrumentName}-${selectedTuning.id}-${i}`}
                                type="button"
                                className={classNames('tuner-string', {
                                    active: activeString === i,
                                    locked: lockedString === i,
                                    'in-tune': activeString === i && inTune,
                                })}
                                aria-pressed={lockedString === i}
                                title={`${prettyNoteName(string.label)}${midiToNote(string.midi).octave} · ${string.frequency.toFixed(1)} Hz`}
                                onClick={() => selectString(i)}
                            >
                                {prettyNoteName(string.label)}
                            </button>
                        )}
                    </div>
                    <p className="tuner-help">{gettext('Tap a string to hear its note and tune to it.')}</p>
                </>
            }

            {isChromatic &&
                <p className="tuner-help">{gettext('Chromatic mode shows the nearest note, so you can tune any instrument to any tuning.')}</p>
            }

            <div className="tuner-reference">
                <button type="button" className={classNames('tuner-link', { 'tuner-link--changed': a4 !== DEFAULT_REFERENCE_PITCH })} aria-expanded={showReferencePitch} onClick={toggleReferencePitch}>
                    {interpolate(gettext('Reference pitch: A4 = %s Hz'), [a4])}
                </button>
                {isSubscriber && showReferencePitch &&
                    <div className="tuner-reference-settings">
                        <div className="tuner-reference-presets">
                            {REFERENCE_PITCH_PRESETS.map(value =>
                                <button key={value} type="button" className={classNames('tuner-pill', { active: value === a4 })} aria-pressed={value === a4} onClick={() => changeReferencePitch(value)}>
                                    {value} Hz
                                </button>
                            )}
                        </div>
                        <div className="tuner-reference-stepper">
                            <button type="button" className="tuner-step" aria-label={gettext('Lower reference pitch')} disabled={a4 <= MIN_REFERENCE_PITCH} onClick={() => changeReferencePitch(a4 - 1)}>−</button>
                            <span className="tuner-reference-value">A4 = {a4} Hz</span>
                            <button type="button" className="tuner-step" aria-label={gettext('Raise reference pitch')} disabled={a4 >= MAX_REFERENCE_PITCH} onClick={() => changeReferencePitch(a4 + 1)}>+</button>
                        </div>
                        <p className="tuner-help">{gettext('Most music is tuned to A4 = 440 Hz. Change it to match a piano or a band that is tuned differently.')}</p>
                    </div>
                }
            </div>

            {status !== 'listening' &&
                <div className="tuner-start">
                    {STATUS_MESSAGES[status] ?
                        <p className="tuner-message">{STATUS_MESSAGES[status]()}</p>
                        :
                        <p className="tuner-message">{gettext('The tuner listens through your microphone. Nothing is recorded or sent anywhere.')}</p>
                    }
                    {status !== 'unsupported' &&
                        <button type="button" className="btn tuner-start-button" disabled={status === 'starting'} onClick={start}>
                            {['idle', 'starting', 'suspended'].includes(status) ? gettext('Start tuner') : gettext('Try again')}
                        </button>
                    }
                </div>
            }
        </div>
    )
}
